import React, { useState } from "react";

export default function AlbumCard({ album }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handlePlayAlbum = () => {
    // Play all tracks in sequence
    const clips = album.tracks?.map(t => t.assets?.clip_wav || t.links?.mp3).filter(Boolean) || [];
    if (clips.length === 0) return;
    
    let idx = 0;
    const playNext = () => {
      if (idx >= clips.length) return;
      const audio = new Audio(clips[idx]);
      idx += 1;
      audio.onended = playNext;
      audio.play().catch(console.error);
    };
    playNext();
  };

  const handleViewSpotify = () => {
    if (album.spotify_album_url) {
      window.open(album.spotify_album_url, '_blank');
    }
  };

  return (
    <div style={{
      backgroundColor: 'var(--surface-color)',
      borderRadius: '12px',
      border: '1px solid var(--border-color)',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}>
      {/* Album Cover */}
      <div style={{ position: 'relative', aspectRatio: '1', overflow: 'hidden' }}>
        <img
          src={album.cover || '/logo.svg'}
          alt={`${album.title} Album Cover`}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.3s ease'
          }}
        />
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'backgroundColor 0.3s ease',
          opacity: 0
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.4)';
          e.currentTarget.style.opacity = 1;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0)';
          e.currentTarget.style.opacity = 0;
        }}>
          <button
            onClick={handlePlayAlbum}
            style={{
              backgroundColor: 'var(--accent-color)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '60px',
              height: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.3s ease',
              fontSize: '1.5rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            ▶️
          </button>
        </div>
      </div>

      {/* Album Info */}
      <div style={{ padding: '1.5rem' }}>
        <h3 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: 'var(--text-primary)',
          margin: '0 0 0.5rem 0',
          lineHeight: '1.3'
        }}>
          {album.title}
        </h3>
        <p style={{
          color: 'var(--text-secondary)',
          margin: '0 0 1rem 0',
          fontSize: '1rem'
        }}>
          {album.artist}
        </p>
        
        {/* Track Count and Release Info */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          fontSize: '0.9rem',
          color: 'var(--text-secondary)'
        }}>
          <span>
            🎵 {album.tracks?.length || 0} tracks
          </span>
          {album.releaseDate && (
            <span>
              📅 {album.releaseDate}
            </span>
          )}
        </div>

        {/* ASCAP Info */}
        {album.ascap_work_id && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            backgroundColor: 'var(--bg-color)',
            borderRadius: '8px',
            fontSize: '0.8rem',
            border: '1px solid var(--border-color)'
          }}>
            <span style={{ fontWeight: '600', color: 'var(--accent-color)' }}>ASCAP:</span>{' '}
            <span style={{ color: 'var(--text-secondary)' }}>{album.ascap_work_id}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
          <button
            onClick={handlePlayAlbum}
            className="professional-button"
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}
          >
            ▶️ Play Album
          </button>
          {album.spotify_album_url && (
            <button
              onClick={handleViewSpotify}
              style={{
                backgroundColor: '#1DB954',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'background-color 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1AA34A';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1DB954';
              }}
            >
              🎧 Spotify
            </button>
          )}
        </div>

        {/* Expandable Track List */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            width: '100%',
            textAlign: 'left',
            fontSize: '0.9rem',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '0.5rem 0',
            borderTop: '1px solid var(--border-color)'
          }}
        >
          <span>📋 Track List</span>
          <span style={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease'
          }}>
            ⌄
          </span>
        </button>

        {isExpanded && (
          <div style={{
            marginTop: '1rem',
            paddingTop: '1rem',
            borderTop: '1px solid var(--border-color)'
          }}>
            {album.tracks?.map((track, idx) => (
              <div key={track.id || idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.5rem 0',
                fontSize: '0.9rem',
                borderBottom: idx < album.tracks.length - 1 ? '1px solid var(--border-color)' : 'none'
              }}>
                <span style={{ color: 'var(--text-primary)' }}>
                  {idx + 1}. {track.track_title}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {track.duration}
                </span>
              </div>
            )) || (
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '0.9rem',
                textAlign: 'center',
                padding: '1rem 0'
              }}>
                No tracks available
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
